import { format } from 'date-fns'
import { type FormikHelpers } from 'formik'
import React from 'react'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { type FileState } from 'src/hooks/fileUpload'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import RegistrationFactory from '../Components/RegistrationFactory'

export type formikValuesType = {
  step1: Omit<
    Api.postLecturerRegistrationParams['body']['step1'],
    'persdoc_uid' | 'high_school_doc_uid' | 'army_status_doc_uid' | 'photo_uid'
  > & {
    persdoc_uid: FileState[]
    high_school_doc_uid: FileState[]
    army_status_doc_uid: FileState[]
    photo_uid: FileState[]
  }
  step2: Omit<Api.postLecturerRegistrationParams['body']['step2'], 'cv_uid' | 'work_exp_uid'> & {
    readonly faculty: string
    readonly program: string
    readonly cv_uid: FileState[]
    readonly work_exp_uid: FileState[]
  }
  step3: Api.postLecturerRegistrationParams['body']['step3']
}

const initialValues: formikValuesType = {
  step1: {
    pers_num: '',
    birth_date: null,
    firstname: '',
    lastname: '',
    firstname_en: '',
    lastname_en: '',
    personal_email: '',
    persdoc_exp_date: null,
    persdoc_uid: [],
    gender: '',
    army_status_doc_uid: [],
    citizenship: '',
    photo_uid: [],
    legal_address: '',
    actual_address: '',
    mobile_phone1: '',
    mobile_phone2: '',
    home_phone: '',
    high_school: '',
    high_school_doc_type: '',
    high_school_finish_date: null,
    high_school_doc_num: '',
    high_school_doc_date: null,
    high_school_doc_uid: [],
    has_limited_abilities: '',
    limited_abilities_text: '',
  },
  step2: {
    cv_uid: [],
    work_exp_uid: [],
    exam_course: '',
    faculty: '',
    program: '',
  },
  step3: {
    agreement: false,
  },
}

export default function LecturersRegistration(): JSX.Element | null {
  const [successfullySubmited, setSuccessfullySubmited] = React.useState(false)

  const t = useTranslatable()
  const locale = useLocale()

  const getFileId = (files: FileState[]): string => files[0]?.remoteFile?.id ?? ''

  const onSubmit = React.useCallback(
    async (values: formikValuesType, helpers: FormikHelpers<formikValuesType>): Promise<void> => {
      const result = await Api.postLecturerRegistration({
        headers: {
          'Accept-Language': locale,
        },
        body: {
          ...values,
          step1: {
            ...values.step1,
            birth_date: format(new Date(values.step1.birth_date!), 'dd/MM/yyyy'),
            persdoc_exp_date: format(new Date(values.step1.persdoc_exp_date!), 'dd/MM/yyyy'),
            high_school_finish_date: format(new Date(values.step1.high_school_finish_date!), 'dd/MM/yyyy'),
            high_school_doc_date: format(new Date(values.step1.high_school_doc_date!), 'dd/MM/yyyy'),
            persdoc_uid: getFileId(values.step1.persdoc_uid),
            high_school_doc_uid: getFileId(values.step1.high_school_doc_uid),
            army_status_doc_uid: getFileId(values.step1.army_status_doc_uid),
            photo_uid: getFileId(values.step1.photo_uid),
          },
          step2: {
            cv_uid: getFileId(values.step2.cv_uid),
            work_exp_uid: getFileId(values.step2.work_exp_uid),
            exam_course: values.step2.exam_course,
          },
        },
      })
      void helpers.setFieldValue('result', result)
      setSuccessfullySubmited(true)
      toast.success(t('registration:registration_success'))
    },
    [locale, t]
  )

  return (
    <RegistrationFactory
      degree="lecturers"
      onSubmit={onSubmit}
      initialValues={initialValues}
      successfullySubmited={successfullySubmited}
    />
  )
}
